/**
 * Embed Theme Types
 * Centralized type definitions for the theme system
 */

// ============================================================================
// Color Types
// ============================================================================

export interface ThemeColorPalette {
  /** Primary brand color */
  primary: string;
  /** Primary color with hover effect */
  primaryHover: string;
  /** Primary color at low opacity for backgrounds */
  primaryLight: string;
  /** Primary color at very low opacity */
  primarySubtle: string;
}

export interface ThemeBackgroundColors {
  /** Main page background */
  page: string;
  /** Card/surface background */
  surface: string;
  /** Elevated surface (modals, dropdowns) */
  elevated: string;
  /** Subtle background for sections */
  subtle: string;
  /** Input field background */
  input: string;
  /** Hover state background */
  hover: string;
}

export interface ThemeTextColors {
  /** Primary text color */
  primary: string;
  /** Secondary/muted text */
  secondary: string;
  /** Tertiary/disabled text */
  tertiary: string;
  /** Inverse text (on primary color) */
  inverse: string;
  /** Link text color */
  link: string;
  /** Error text color */
  error: string;
  /** Success text color */
  success: string;
}

export interface ThemeBorderColors {
  /** Default border color */
  default: string;
  /** Subtle border color */
  subtle: string;
  /** Strong border color */
  strong: string;
  /** Focus ring-3 color */
  focus: string;
  /** Error border color */
  error: string;
}

export interface ThemeColors {
  palette: ThemeColorPalette;
  background: ThemeBackgroundColors;
  text: ThemeTextColors;
  border: ThemeBorderColors;
}

// ============================================================================
// Typography Types
// ============================================================================

export interface ThemeFontSize {
  xs: string;
  sm: string;
  base: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
}

export interface ThemeFontWeight {
  normal: number;
  medium: number;
  semibold: number;
  bold: number;
}

export interface ThemeLineHeight {
  tight: number;
  normal: number;
  relaxed: number;
}

export interface ThemeTypography {
  fontFamily: string;
  fontSize: ThemeFontSize;
  fontWeight: ThemeFontWeight;
  lineHeight: ThemeLineHeight;
}

// ============================================================================
// Spacing Types
// ============================================================================

export interface ThemeSpacing {
  /** 0px */
  none: string;
  /** 2px */
  '0.5': string;
  /** 4px */
  '1': string;
  /** 6px */
  '1.5': string;
  /** 8px */
  '2': string;
  /** 10px */
  '2.5': string;
  /** 12px */
  '3': string;
  /** 16px */
  '4': string;
  /** 20px */
  '5': string;
  /** 24px */
  '6': string;
  /** 32px */
  '8': string;
  /** 40px */
  '10': string;
  /** 48px */
  '12': string;
}

// ============================================================================
// Border Types
// ============================================================================

export interface ThemeBorderRadius {
  none: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  full: string;
}

export interface ThemeBorderWidth {
  none: string;
  thin: string;
  base: string;
  thick: string;
}

// ============================================================================
// Shadow Types
// ============================================================================

export interface ThemeShadow {
  none: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
}

// ============================================================================
// Animation Types
// ============================================================================

export interface ThemeAnimation {
  duration: {
    fast: string;
    normal: string;
    slow: string;
  };
  easing: {
    default: string;
    easeIn: string;
    easeOut: string;
    easeInOut: string;
  };
}

// ============================================================================
// Component-Specific Types
// ============================================================================

export interface ThemeComponentStyles {
  button: {
    height: {
      sm: string;
      md: string;
      lg: string;
    };
    padding: {
      sm: string;
      md: string;
      lg: string;
    };
    borderRadius: string;
  };
  input: {
    height: string;
    padding: string;
    borderRadius: string;
  };
  card: {
    padding: string;
    borderRadius: string;
  };
  avatar: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
}

// ============================================================================
// Main Theme Type
// ============================================================================

export interface EmbedTheme {
  /** Theme mode */
  mode: 'light' | 'dark';
  /** Theme colors */
  colors: ThemeColors;
  /** Typography settings */
  typography: ThemeTypography;
  /** Spacing scale */
  spacing: ThemeSpacing;
  /** Border radius scale */
  borderRadius: ThemeBorderRadius;
  /** Border width scale */
  borderWidth: ThemeBorderWidth;
  /** Shadow scale */
  shadow: ThemeShadow;
  /** Animation settings */
  animation: ThemeAnimation;
  /** Component-specific styles */
  components: ThemeComponentStyles;
}

// ============================================================================
// Theme Configuration (User Input)
// ============================================================================

export interface EmbedThemeConfig {
  /** Theme mode: 'light' or 'dark' */
  mode?: 'light' | 'dark';
  /** Primary brand color (hex) */
  primaryColor?: string;
  /** Custom font family */
  fontFamily?: string;
  /** Border radius scale: 'none', 'sm', 'md', 'lg', 'full' */
  borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  /** Custom color overrides */
  colors?: Partial<{
    background: Partial<ThemeBackgroundColors>;
    text: Partial<ThemeTextColors>;
    border: Partial<ThemeBorderColors>;
  }>;
}

// ============================================================================
// Theme Preset Type
// ============================================================================

export interface ThemePreset {
  name: string;
  config: EmbedThemeConfig;
}

// ============================================================================
// Advanced Widget Configuration Types
// ============================================================================

/**
 * Background style configuration
 */
export interface BackgroundConfig {
  /** Background type */
  type: 'solid' | 'gradient' | 'frosted';
  /** Primary background color (hex) */
  color: string;
  /** Gradient start color (for gradient type) */
  gradientFrom?: string;
  /** Gradient end color (for gradient type) */
  gradientTo?: string;
  /** Gradient angle in degrees (for gradient type) */
  gradientAngle?: number;
  /** Background opacity (0-100) */
  opacity: number;
  /** Blur amount in pixels (for frosted type) */
  blur?: number;
}

/**
 * Message bubble style configuration
 */
export interface MessageBubbleConfig {
  /** Background color */
  background: string;
  /** Text color */
  textColor: string;
  /** Border radius in rem */
  borderRadius: number;
  /** Shadow intensity */
  shadow: 'none' | 'sm' | 'md';
}

/**
 * Message bubbles configuration
 */
export interface MessageBubblesConfig {
  /** User message bubble style */
  user: MessageBubbleConfig;
  /** Agent message bubble style */
  agent: MessageBubbleConfig;
  /** Max width percentage (50-100) */
  maxWidth: number;
  /** Padding density */
  padding: 'compact' | 'normal' | 'spacious';
}

/**
 * Widget position configuration
 */
export interface PositionConfig {
  /** Position placement */
  placement: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Horizontal offset in pixels */
  offsetX: number;
  /** Vertical offset in pixels */
  offsetY: number;
  /** Z-index for stacking */
  zIndex: number;
}

/**
 * Launcher button configuration
 */
export interface LauncherConfig {
  /** Icon type */
  icon: 'robot' | 'sparkle' | 'brain' | 'chat' | 'message' | 'custom';
  /** Custom icon URL (for custom type) */
  customIconUrl?: string;
  /** Button size in pixels */
  size: number;
  /** Background color */
  background: string;
  /** Hover background color */
  hoverBackground: string;
  /** Shadow intensity */
  shadow: 'none' | 'sm' | 'md' | 'lg';
  /** Enable pulse animation */
  pulse: boolean;
  /** Tooltip text */
  tooltip: string;
  /** Icon color */
  iconColor: string;
}

/**
 * Animation appear configuration
 */
export interface AppearAnimationConfig {
  /** Delay before appearing in seconds */
  delay: number;
  /** Animation type */
  animation: 'none' | 'fade' | 'slide-up' | 'slide-down' | 'scale' | 'bounce';
  /** Animation duration in ms */
  duration: number;
  /** Easing function */
  easing: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'spring';
}

/**
 * Message animation configuration
 */
export interface MessageAnimationConfig {
  /** Enable staggered animation */
  staggered: boolean;
  /** Delay between messages in ms */
  staggerDelay: number;
  /** Animation type */
  animation: 'none' | 'fade' | 'slide' | 'scale';
}

/**
 * Typing indicator configuration
 */
export interface TypingIndicatorConfig {
  /** Indicator style */
  style: 'dots' | 'pulse' | 'wave' | 'shimmer';
  /** Indicator color */
  color: string;
}

/**
 * Animation configuration
 */
export interface AnimationConfig {
  /** Widget appear animation */
  appear: AppearAnimationConfig;
  /** Message animations */
  messages: MessageAnimationConfig;
  /** Typing indicator */
  typing: TypingIndicatorConfig;
  /** Transition duration in ms */
  transitionDuration: number;
}

/**
 * Header configuration
 */
export interface HeaderConfig {
  /** Show header */
  show: boolean;
  /** Header title (overrides agent name) */
  title?: string;
  /** Header subtitle */
  subtitle?: string;
  /** Background color */
  background: string;
  /** Text color */
  textColor: string;
  /** Enable blur-sm effect */
  blur: boolean;
}

/**
 * Input area configuration
 */
export interface InputConfig {
  /** Background color */
  background: string;
  /** Text color */
  textColor: string;
  /** Border radius in rem */
  borderRadius: number;
  /** Placeholder text */
  placeholder: string;
  /** Show focus ring-3 */
  focusRing: boolean;
  /** Focus ring-3 color */
  focusColor: string;
}

/**
 * Login UI text configuration
 */
export interface LoginTextConfig {
  /** Login title */
  title: string;
  /** Login subtitle */
  subtitle: string;
  /** Email field label */
  emailLabel: string;
  /** Login button text */
  buttonText: string;
  /** Footer text */
  footerText: string;
}

/**
 * Branding configuration
 */
export interface BrandingConfig {
  /** Show powered by branding */
  show: boolean;
  /** Branding text */
  text: string;
  /** Branding link URL */
  link: string;
}

/**
 * Content customization configuration
 */
export interface ContentConfig {
  /** Welcome message shown on first open */
  welcomeMessage: string;
  /** Input placeholder text */
  placeholder: string;
  /** Login UI text customization */
  login: LoginTextConfig;
  /** Branding customization */
  branding: BrandingConfig;
}

/**
 * Behavior configuration
 */
export interface BehaviorConfig {
  /** Auto-open widget on page load */
  autoOpen: boolean;
  /** Delay before auto-open in seconds */
  autoOpenDelay: number;
  /** Close widget when clicking outside */
  closeOnOutsideClick: boolean;
  /** Remember open/closed state */
  persistState: boolean;
  /** Enable sound effects */
  soundEffects: boolean;
}

// ============================================================================
// Design Agency Configuration Types
// ============================================================================

/**
 * Typography configuration for design system alignment
 */
export interface TypographyConfig {
  /** Font family - 'system' uses system fonts, or specify custom font name */
  fontFamily: 'system' | 'inter' | 'roboto' | 'open-sans' | 'lato' | 'poppins' | 'custom';
  /** Custom font family CSS value (when fontFamily is 'custom') */
  customFontFamily?: string;
  /** Base font size in pixels */
  baseFontSize: number;
  /** Header font size in pixels */
  headerFontSize: number;
  /** Message font size in pixels */
  messageFontSize: number;
  /** Input font size in pixels */
  inputFontSize: number;
  /** Line height multiplier */
  lineHeight: number;
  /** Letter spacing in em */
  letterSpacing: number;
  /** Font weight for normal text */
  fontWeight: 400 | 500 | 600;
  /** Font weight for bold/header text */
  fontWeightBold: 500 | 600 | 700 | 800;
}

/**
 * Avatar display configuration
 */
export interface AvatarConfig {
  /** Show avatar for agent messages */
  showAgentAvatar: boolean;
  /** Show avatar for user messages */
  showUserAvatar: boolean;
  /** Avatar shape */
  shape: 'circle' | 'rounded-sm' | 'square';
  /** Avatar size in pixels */
  size: number;
  /** Avatar border width in pixels */
  borderWidth: number;
  /** Avatar border color */
  borderColor: string;
  /** Avatar position relative to message */
  position: 'top' | 'center' | 'bottom';
  /** Gap between avatar and message in pixels */
  gap: number;
}

/**
 * Spacing configuration for precise layout control
 */
export interface SpacingConfig {
  /** Gap between messages in pixels */
  messageGap: number;
  /** Gap between message groups (same sender) in pixels */
  messageGroupGap: number;
  /** Widget inner padding in pixels */
  widgetPadding: number;
  /** Header padding in pixels */
  headerPadding: number;
  /** Input area padding in pixels */
  inputPadding: number;
  /** Message bubble inner padding in pixels */
  messagePadding: number;
}

/**
 * Scrollbar styling configuration
 */
export interface ScrollbarConfig {
  /** Scrollbar style */
  style: 'default' | 'thin' | 'hidden' | 'custom';
  /** Scrollbar width in pixels (for custom) */
  width: number;
  /** Scrollbar track color */
  trackColor: string;
  /** Scrollbar thumb color */
  thumbColor: string;
  /** Scrollbar thumb hover color */
  thumbHoverColor: string;
  /** Scrollbar thumb border radius in pixels */
  thumbRadius: number;
  /** Show scroll shadow-sm at top when scrolled */
  showScrollShadow: boolean;
}

/**
 * Timestamp display configuration
 */
export interface TimestampConfig {
  /** Show timestamps on messages */
  show: boolean;
  /** Timestamp format */
  format: 'relative' | 'time' | 'datetime' | 'hidden';
  /** Timestamp position */
  position: 'inline' | 'below' | 'hover';
  /** Timestamp font size in pixels */
  fontSize: number;
  /** Timestamp color */
  color: string;
}

/**
 * Send button configuration
 */
export interface SendButtonConfig {
  /** Button style variant */
  variant: 'filled' | 'outline' | 'ghost' | 'icon-only';
  /** Button shape */
  shape: 'circle' | 'rounded-sm' | 'square';
  /** Button size in pixels */
  size: number;
  /** Send animation on click */
  animation: 'none' | 'pulse' | 'scale' | 'slide' | 'rotate';
  /** Icon style */
  icon: 'arrow' | 'send' | 'paper-plane';
  /** Show button only when input has text */
  showOnlyWithText: boolean;
}

/**
 * Micro-interactions configuration
 */
export interface MicroInteractionsConfig {
  /** Enable hover effects on messages */
  messageHover: boolean;
  /** Message hover effect type */
  messageHoverEffect: 'none' | 'lift' | 'glow' | 'highlight';
  /** Enable hover effects on buttons */
  buttonHover: boolean;
  /** Button hover scale (1 = no scale) */
  buttonHoverScale: number;
  /** Enable focus ring-3 animations */
  focusAnimation: boolean;
  /** Enable smooth scrolling */
  smoothScroll: boolean;
  /** Enable scroll-to-bottom button */
  scrollToBottom: boolean;
  /** Scroll-to-bottom button style */
  scrollToBottomStyle: 'circle' | 'pill' | 'minimal';
}

/**
 * Border configuration for fine control
 */
export interface BorderStyleConfig {
  /** Widget border width in pixels */
  widgetBorderWidth: number;
  /** Widget border color */
  widgetBorderColor: string;
  /** Widget border style */
  widgetBorderStyle: 'none' | 'solid' | 'dashed';
  /** Header border (bottom) */
  headerBorder: boolean;
  /** Input area border (top) */
  inputBorder: boolean;
  /** Message bubble border width */
  messageBorderWidth: number;
  /** User message border color */
  userMessageBorderColor: string;
  /** Agent message border color */
  agentMessageBorderColor: string;
}

/**
 * Typing indicator detailed configuration
 */
export interface TypingIndicatorDetailedConfig {
  /** Indicator style */
  style: 'dots' | 'pulse' | 'wave' | 'shimmer' | 'bounce' | 'typing-text';
  /** Primary color */
  color: string;
  /** Number of dots (for dot-based styles) */
  dotCount: 3 | 4 | 5;
  /** Dot size in pixels */
  dotSize: number;
  /** Spacing between dots in pixels */
  dotSpacing: number;
  /** Animation speed multiplier (0.5 = slow, 1 = normal, 2 = fast) */
  speed: number;
  /** Text to show when style is 'typing-text' */
  typingText: string;
  /** Show indicator inside a bubble */
  showInBubble: boolean;
  /** Bubble background color */
  bubbleBackground: string;
}

/**
 * Message skeleton loading configuration
 */
export interface SkeletonLoadingConfig {
  /** Enable skeleton loading for messages */
  enabled: boolean;
  /** Skeleton animation style */
  animation: 'pulse' | 'shimmer' | 'wave' | 'none';
  /** Base color for skeleton */
  baseColor: string;
  /** Highlight/shimmer color */
  highlightColor: string;
  /** Animation duration in ms */
  animationDuration: number;
  /** Number of skeleton lines */
  lineCount: 2 | 3 | 4;
  /** Randomize line widths for natural look */
  randomWidths: boolean;
  /** Border radius for skeleton lines */
  borderRadius: number;
}

/**
 * Widget initial loading state configuration
 */
export interface WidgetLoadingConfig {
  /** Show loading state when widget initializes */
  showInitialLoading: boolean;
  /** Loading indicator style */
  style: 'spinner' | 'dots' | 'skeleton' | 'logo-pulse' | 'progress-bar';
  /** Spinner/dot color */
  color: string;
  /** Spinner size in pixels */
  size: number;
  /** Loading text to display */
  text: string;
  /** Show loading text */
  showText: boolean;
  /** Background overlay opacity (0-100) */
  overlayOpacity: number;
  /** Minimum display time in ms (prevents flash) */
  minDisplayTime: number;
}

/**
 * Send button loading state configuration
 */
export interface SendButtonLoadingConfig {
  /** Loading indicator style when sending */
  style: 'spinner' | 'dots' | 'pulse' | 'progress';
  /** Loading indicator color */
  color: string;
  /** Disable button while sending */
  disableWhileSending: boolean;
  /** Show sending text instead of icon */
  showSendingText: boolean;
  /** Sending text */
  sendingText: string;
}

/**
 * Response streaming indicator configuration
 */
export interface StreamingIndicatorConfig {
  /** Show streaming indicator */
  enabled: boolean;
  /** Indicator style */
  style: 'cursor' | 'dots' | 'fade' | 'highlight';
  /** Cursor character (for cursor style) */
  cursorChar: string;
  /** Cursor blink animation */
  cursorBlink: boolean;
  /** Cursor color */
  cursorColor: string;
  /** Fade highlight color (for fade/highlight styles) */
  highlightColor: string;
}

/**
 * Comprehensive loading indicator configuration
 */
export interface LoadingIndicatorConfig {
  /** Typing indicator (when agent is composing) */
  typingIndicator: TypingIndicatorDetailedConfig;
  /** Skeleton loading for message placeholders */
  skeleton: SkeletonLoadingConfig;
  /** Widget initial loading state */
  widgetLoading: WidgetLoadingConfig;
  /** Send button loading state */
  sendButton: SendButtonLoadingConfig;
  /** Response streaming indicator */
  streaming: StreamingIndicatorConfig;
}

/**
 * Complete advanced widget configuration
 */
export interface AdvancedWidgetConfig {
  /** Background configuration */
  background: BackgroundConfig;
  /** Message bubbles configuration */
  messageBubbles: MessageBubblesConfig;
  /** Position configuration */
  position: PositionConfig;
  /** Launcher button configuration */
  launcher: LauncherConfig;
  /** Animation configuration */
  animation: AnimationConfig;
  /** Header configuration */
  header: HeaderConfig;
  /** Input area configuration */
  input: InputConfig;
  /** Content configuration */
  content: ContentConfig;
  /** Behavior configuration */
  behavior: BehaviorConfig;
  /** Typography configuration */
  typography: TypographyConfig;
  /** Avatar configuration */
  avatar: AvatarConfig;
  /** Spacing configuration */
  spacing: SpacingConfig;
  /** Scrollbar configuration */
  scrollbar: ScrollbarConfig;
  /** Timestamp configuration */
  timestamp: TimestampConfig;
  /** Send button configuration */
  sendButton: SendButtonConfig;
  /** Micro-interactions configuration */
  microInteractions: MicroInteractionsConfig;
  /** Border style configuration */
  borderStyle: BorderStyleConfig;
  /** Loading indicator configuration */
  loadingIndicator: LoadingIndicatorConfig;
}

/**
 * Default values for advanced widget configuration
 */
export const DEFAULT_ADVANCED_CONFIG: AdvancedWidgetConfig = {
  background: {
    type: 'solid',
    color: '#ffffff',
    opacity: 100,
    blur: 12,
    gradientAngle: 135,
  },
  messageBubbles: {
    user: {
      // Empty → inherit the widget accent (primaryColor) at render so the user
      // bubble tracks the theme like the in-app FloatingAssistant. A non-empty
      // value (set via Advanced → Your message color) overrides it.
      background: '',
      textColor: '#ffffff',
      borderRadius: 1.25,
      shadow: 'sm',
    },
    agent: {
      // Empty → follow the theme (light grey in light mode, slate in dark), so a
      // dark widget doesn't get a light answer box. Set via Advanced to override.
      background: '',
      textColor: '',
      borderRadius: 1.25,
      shadow: 'none',
    },
    maxWidth: 85,
    padding: 'normal',
  },
  position: {
    placement: 'bottom-right',
    offsetX: 20,
    offsetY: 20,
    zIndex: 9999,
  },
  launcher: {
    icon: 'message',
    size: 56,
    background: '#3b82f6',
    hoverBackground: '#2563eb',
    shadow: 'lg',
    pulse: false,
    tooltip: 'Chat with us',
    iconColor: '#ffffff',
  },
  animation: {
    appear: {
      delay: 0,
      animation: 'scale',
      duration: 200,
      easing: 'ease-out',
    },
    messages: {
      staggered: true,
      staggerDelay: 50,
      animation: 'fade',
    },
    typing: {
      style: 'dots',
      color: '#6b7280',
    },
    transitionDuration: 200,
  },
  header: {
    show: true,
    background: '#ffffff',
    textColor: '#1f2937',
    blur: false,
  },
  input: {
    background: '#ffffff',
    textColor: '#1f2937',
    borderRadius: 1.5,
    placeholder: 'Type a message...',
    focusRing: true,
    focusColor: '#3b82f6',
  },
  content: {
    welcomeMessage: '',
    placeholder: 'Type a message...',
    login: {
      title: 'Welcome',
      subtitle: 'Sign in to continue',
      emailLabel: 'Email',
      buttonText: 'Continue',
      footerText: '',
    },
    branding: {
      show: true,
      text: 'Powered by Bolder',
      link: 'https://bldr.it',
    },
  },
  behavior: {
    autoOpen: false,
    autoOpenDelay: 3,
    closeOnOutsideClick: true,
    persistState: true,
    soundEffects: false,
  },
  typography: {
    fontFamily: 'system',
    baseFontSize: 14,
    headerFontSize: 16,
    messageFontSize: 14,
    inputFontSize: 14,
    lineHeight: 1.5,
    letterSpacing: 0,
    fontWeight: 400,
    fontWeightBold: 600,
  },
  avatar: {
    showAgentAvatar: true,
    showUserAvatar: false,
    shape: 'circle',
    size: 32,
    borderWidth: 0,
    borderColor: '#e5e7eb',
    position: 'top',
    gap: 8,
  },
  spacing: {
    messageGap: 8,
    messageGroupGap: 16,
    widgetPadding: 16,
    headerPadding: 16,
    inputPadding: 16,
    messagePadding: 12,
  },
  scrollbar: {
    style: 'thin',
    width: 6,
    trackColor: 'transparent',
    thumbColor: '#d1d5db',
    thumbHoverColor: '#9ca3af',
    thumbRadius: 3,
    showScrollShadow: true,
  },
  timestamp: {
    show: true,
    format: 'relative',
    position: 'hover',
    fontSize: 11,
    color: '#9ca3af',
  },
  sendButton: {
    variant: 'filled',
    shape: 'circle',
    size: 36,
    animation: 'scale',
    icon: 'arrow',
    showOnlyWithText: true,
  },
  microInteractions: {
    messageHover: true,
    messageHoverEffect: 'lift',
    buttonHover: true,
    buttonHoverScale: 1.05,
    focusAnimation: true,
    smoothScroll: true,
    scrollToBottom: true,
    scrollToBottomStyle: 'circle',
  },
  borderStyle: {
    widgetBorderWidth: 1,
    widgetBorderColor: '#e5e7eb',
    widgetBorderStyle: 'solid',
    headerBorder: true,
    inputBorder: true,
    messageBorderWidth: 0,
    userMessageBorderColor: 'transparent',
    agentMessageBorderColor: 'transparent',
  },
  loadingIndicator: {
    typingIndicator: {
      style: 'dots',
      color: '#6b7280',
      dotCount: 3,
      dotSize: 8,
      dotSpacing: 4,
      speed: 1,
      typingText: 'Typing...',
      showInBubble: true,
      bubbleBackground: '#f3f4f6',
    },
    skeleton: {
      enabled: true,
      animation: 'shimmer',
      baseColor: '#e5e7eb',
      highlightColor: '#f3f4f6',
      animationDuration: 1500,
      lineCount: 3,
      randomWidths: true,
      borderRadius: 4,
    },
    widgetLoading: {
      showInitialLoading: true,
      style: 'spinner',
      color: '#3b82f6',
      size: 32,
      text: 'Loading...',
      showText: false,
      overlayOpacity: 80,
      minDisplayTime: 300,
    },
    sendButton: {
      style: 'spinner',
      color: '#ffffff',
      disableWhileSending: true,
      showSendingText: false,
      sendingText: 'Sending...',
    },
    streaming: {
      enabled: true,
      style: 'cursor',
      cursorChar: '▋',
      cursorBlink: true,
      cursorColor: '#3b82f6',
      highlightColor: 'rgba(59, 130, 246, 0.1)',
    },
  },
};
